import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Tab,
    Tabs,
    Typography,
    IconButton,
    CircularProgress,
    Paper,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faFileUpload, 
    faCode, 
    faFile,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { UploadFormData } from '../types';

interface UploadModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: UploadFormData) => void;
    initialData?: UploadFormData;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`upload-tabpanel-${index}`}
            aria-labelledby={`upload-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export const UploadModal: React.FC<UploadModalProps> = ({ open, onClose, onSubmit, initialData }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [templateName, setTemplateName] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (initialData) {
            setTemplateName(initialData.label);
            setHtmlContent(initialData.html_content);
            setActiveTab(1); // Switch to HTML editor tab when editing
        } else {
            resetForm();
        }
    }, [initialData, open]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setHtmlContent(e.target?.result as string);
            };
            reader.readAsText(file);
        }
    };

    const handleCancelFile = () => {
        setSelectedFile(null);
        setHtmlContent('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (templateName && htmlContent) {
            setIsSubmitting(true);
            try {
                await onSubmit({
                    label: templateName,
                    html_content: htmlContent,
                });
                resetForm();
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const resetForm = () => {
        setTemplateName('');
        setHtmlContent('');
        setSelectedFile(null);
        setActiveTab(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {initialData ? 'Edit HTML/Text Template' : 'Add HTML/Text Template'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Template Name"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        margin="normal"
                        required
                        autoFocus
                        sx={{ mb: 3 }}
                    />

                    {!initialData && (
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                            <Tabs value={activeTab} onChange={handleTabChange}>
                                <Tab
                                    icon={<FontAwesomeIcon icon={faFileUpload} />}
                                    label="Upload File"
                                    id="upload-tab-0"
                                />
                                <Tab
                                    icon={<FontAwesomeIcon icon={faCode} />}
                                    label="Paste HTML/Text"
                                    id="upload-tab-1"
                                />
                            </Tabs>
                        </Box>
                    )}

                    {!initialData && (
                        <TabPanel value={activeTab} index={0}>
                            {!selectedFile ? (
                                <Box
                                    sx={{
                                        border: '2px dashed',
                                        borderColor: 'primary.light',
                                        borderRadius: 2,
                                        p: 3,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        bgcolor: 'background.paper',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                            opacity: 0.8,
                                        },
                                    }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        type="file"
                                        accept=".html,.htm,.txt"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        ref={fileInputRef}
                                    />
                                    <FontAwesomeIcon
                                        icon={faFileUpload}
                                        size="2x"
                                        style={{ marginBottom: '1rem', color: '#1d4ed8' }}
                                    />
                                    <Typography color="primary.main">
                                        Click to upload or drag and drop HTML/Text file here
                                    </Typography>
                                </Box>
                            ) : (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        bgcolor: 'primary.light',
                                        opacity: 0.8,
                                    }}
                                >
                                    <FontAwesomeIcon 
                                        icon={faFile} 
                                        size="lg"
                                        style={{ color: '#1d4ed8' }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="primary.dark">
                                            {selectedFile.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {(selectedFile.size / 1024).toFixed(1)} KB
                                        </Typography>
                                    </Box>
                                    <IconButton 
                                        onClick={handleCancelFile}
                                        size="small"
                                        sx={{ 
                                            color: 'primary.dark',
                                            '&:hover': {
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                            }
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </IconButton>
                                </Paper>
                            )}
                        </TabPanel>
                    )}

                    <TabPanel value={activeTab} index={1}>
                        <TextField
                            fullWidth
                            multiline
                            rows={15}
                            label="HTML/Text Content"
                            value={htmlContent}
                            onChange={(e) => setHtmlContent(e.target.value)}
                            required={activeTab === 1 || initialData !== undefined}
                            sx={{
                                '& .MuiInputBase-root': {
                                    fontFamily: 'monospace',
                                },
                            }}
                        />
                    </TabPanel>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!templateName || !htmlContent || isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
                    >
                        {initialData ? 'Save Changes' : 'Create Template'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}; 